import { types, getEnv, flow, SnapshotOut, Instance } from 'mobx-state-tree';
import { observable } from 'mobx';
import { CategoryStore } from './category';
import { IAppEnv } from '../appEnvironment';
import { pushToEventList, IPushToEventListParams, isDateFilterParams, isTimeFilterParams, prepareFromTo, TimeDateFilterOptions, IFilterParams, prepareRates } from '../routing';
import { dateFormatter, DateFormat, translation, translationForMts } from 'easit--localization';
import { IGroupedApiCtx, contract, CancelError, errorIsCancel, ProgressStateModelForStore, ProgressStateModel, rootCategoryId, FavoriteOffers, IGetWebStandardCategoriesParams } from 'eTip--common';
import { ISportsBettingApi } from '../api/api';
import { getCanShowLiveInPrematchCommon } from './eventListModel';

// export interface IFilterParams {
//     view: string;
//     categoryId: string;
//     filter?: number;
//     hours?: number;
//     date?: Date;
//     searchBy?: string;
// }

export enum FavoriteOfferEnum {
    Category = 1,
    Event = 2
}

export enum FilterType {
    none,
    today,
    tommorow,
    nextWeek,
    byDay,
    byWeekDay,
    byTime,
    timeDateFilter
}

export const TodayTomorowNextWeekFilterCountsStore = types.model('TodayTomorowWeekFilterCounts', {
    today: types.maybeNull(types.number),
    tommorow: types.maybeNull(types.number),
    week: types.maybeNull(types.number)
});

export const FavoriteStore = types.model('FavoriteStore', {
    id: types.string,
    name: types.string,
    path: types.optional(types.array(types.string), []),
    iconId: types.maybeNull(types.number),
    type: types.optional(types.number, FavoriteOfferEnum.Category), // FavoriteOfferEnum
    isLoading: types.optional(types.boolean, false),
    hasLive: types.boolean
});

export const RateFilterMinOddsRateInInput = 1;
export const RateFilterMaxOddsRateInInput = 1000;

export const RateFilterInputZones = [{ min: 1, max: 3.49, step: 0.01 }, { min: 3.5, max: 9.45, step: 0.05 }, { min: 9.5, max: 29.5, step: 0.5 }, { min: 30, max: 1000, step: 1 }];

export const CategoryMenuStoreKey = 'CategoryMenuStore';
export const CategoryMenuStore = types
    .model(CategoryMenuStoreKey, {
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        categories: types.optional(types.map(CategoryStore), {}),
        expandedCategories: types.optional(types.map(types.boolean), {}),
        favorites: types.optional(types.map(FavoriteStore), {}),
        isFavoritesExpanded: types.maybeNull(types.boolean),
        activeCategoryKey: types.maybeNull(types.string),
        todayTommorowWeekFilterCounts: types.optional(TodayTomorowNextWeekFilterCountsStore, {}),
        isExpendedMoreFilter: types.maybeNull(types.boolean),
        isExpendedWeekDayFilter: types.maybeNull(types.boolean),
        isExpandedDateFilter: types.maybeNull(types.boolean),
        isExpandedTimeFilter: types.maybeNull(types.boolean),
        rootCategoryIds: types.optional(types.array(types.string), []),
        timeStamp: types.maybeNull(types.number),
        rateFrom: types.maybeNull(types.number),
        rateTo: types.maybeNull(types.number),
        rateFromInBar: types.maybeNull(types.number),
        rateToInBar: types.maybeNull(types.number),
        rateFromInInput: types.maybeNull(types.number),
        rateToInInput: types.maybeNull(types.number),
        enabledCalendarDays: types.maybeNull(types.array(types.string))
    })
    .views(self => {
        const env = getEnv<IAppEnv>(self);
        const t = translationForMts();
        let timeOptionsForFilter = __IsMTSTipos__ ? //see also dateFilter.ts
            [
                { value: TimeDateFilterOptions.Plus1, label: '+1' },
                { value: TimeDateFilterOptions.Plus3, label: '+3' },
                { value: TimeDateFilterOptions.Plus6, label: '+6' },
                { value: TimeDateFilterOptions.Today, label: t.prematch_TimeFilterToday },
                { value: TimeDateFilterOptions.Tomorrow, label: t.tomorrow },
                { value: TimeDateFilterOptions.Plus7d, label: '+ 7 dní' /* Tipos only, leaving in SK */ }
            ] :
            [
                { value: TimeDateFilterOptions.Plus3, label: '+3' },
                { value: TimeDateFilterOptions.Plus6, label: '+6' },
                { value: TimeDateFilterOptions.Today, label: t.prematch_TimeFilterToday },
                { value: TimeDateFilterOptions.TodayTomorrow, label: t.prematch_TimeFilterTodayTomorrow },
                { value: TimeDateFilterOptions.Weekend, label: t.weekend }
            ]

        return {
            get showFavoritesTooltip() {
                return env.loggedUserId === 0;
            },
            get rootCategoryId() {
                return rootCategoryId;
            },
            get noCategories() {
                return self.categories.size === 0;
            },
            get now() {
                return env.dateTimeService.now();
            },
            get refreshTime() {
                return env.settings.categoriesTreeRefreshTime;
            },

            isFavorite(categoryId: string, type: FavoriteOfferEnum) {
                return self.favorites.has(categoryId) && self.favorites.get(categoryId).type === type;
            },
            activeFilter(routeParams: IPushToEventListParams) {
                switch (routeParams.filter) {
                    case FilterType.byDay:
                        return isDateFilterParams(routeParams.filterParams) ? dateFormatter.formatDate(routeParams.filterParams.date, DateFormat.DDMMYY) : '';
                    case FilterType.byWeekDay:
                        return isDateFilterParams(routeParams.filterParams) ? dateFormatter.formatDate(routeParams.filterParams.date, DateFormat.DayName) : '';
                    case FilterType.byTime:
                        const hours = isTimeFilterParams(routeParams.filterParams) && routeParams.filterParams.hours;
                        switch (hours) {
                            case 1:
                                return `1 ${translation().hour}`;
                            case 3:
                                return `3 ${translation().three_hours}`;
                            default:
                                return isTimeFilterParams(routeParams.filterParams) ? `${hours} ${translation().hours}` : '';
                        }
                    default:
                        return '';
                }
            },
            searchActiveParents(categoryKey: string): string[] {
                const activeParents: string[] = [];
                let activeCategory = self.categories.get(`${categoryKey}`);
                activeParents.push(categoryKey);
                while (activeCategory && activeCategory.parentKey) {
                    activeParents.push(activeCategory.parentKey);
                    activeCategory = self.categories.get(`${activeCategory.parentKey}`);
                }
                // activeParents.pop();
                return activeParents;
            },
            get isLoggedUser(): boolean {
                return env.loggedUserId > 0;
            },

            get favoritesCount(): number {
                return self.favorites.size;
            },

            get rootVirtualCategoryIds(): string[] {
                return self.rootCategoryIds.filter(id => self.categories.get(id).isVirtual);
            },

            get rootNotVirtualCategoryIds(): string[] {
                return self.rootCategoryIds.filter(id => !self.categories.get(id).isVirtual);
            },

            get rootMtsTopCategoryIds(): string[] {
                let result: string[] = [];
                self.rootCategoryIds.some((id) => {
                    if (self.categories.get(id).isVirtual) {
                        result.push(id);
                        return false;
                    }
                    return true;
                });
                return result;
            },
            get rootMtsNotTopCategoryIds(): string[] {
                let result: string[] = [];
                let wasNormal = false;
                self.rootCategoryIds.forEach((id) => {
                    if (wasNormal) {
                        result.push(id);
                    }
                    else if (!self.categories.get(id).isVirtual) {
                        wasNormal = true;
                        result.push(id);
                    }
                });
                return result;
            },
            get timeOptionsForFilter(): { value: TimeDateFilterOptions, label: string }[] {
                return timeOptionsForFilter;
            }
        };
    })
    .actions(self => {
        const env = getEnv<IAppEnv>(self);
        let synchroCtx: IGroupedApiCtx<ISportsBettingApi> | null = null;
        let favoriteItemsUpdateRunningQueries: Object[] = [];

        function mapBaseCategoryToStoreInitObject(level: number, base: contract.ICategoryBase, parentKey: string = ''): Instance<typeof CategoryStore> {
            return <Instance<typeof CategoryStore>>{
                id: base.Id,
                level: level,
                parentId: base.ParentId,
                name: base.Name,
                backgroundColor: base.BackgroundColor,
                parentKey: parentKey,
                expanded: self.activeCategoryKey && self.activeCategoryKey.includes(base.Id),
                hasLive: base.HasLive
            };
        }

        function mapFourthLevelCategory(category: contract.IFourthLevelCategory, parentKey: string): Instance<typeof CategoryStore> {
            return CategoryStore.create(<SnapshotOut<typeof CategoryStore>>mapBaseCategoryToStoreInitObject(4, category.Base, parentKey));
        }

        function mapThirdLevelCategory(category: contract.IThirdLevelCategory, parentKey: string): Instance<typeof CategoryStore> {
            return CategoryStore.create(<SnapshotOut<typeof CategoryStore>>
                Object.assign(
                    {
                        categoriesIds: observable(
                            category.Categories.map(c => {
                                self.categories.set(
                                    `${parentKey}c${category.Base.Id}c${c.Base.Id}`,
                                    mapFourthLevelCategory(c, `${parentKey}c${category.Base.Id}`)
                                );
                                return c.Base.Id;
                            })
                        )
                    },
                    mapBaseCategoryToStoreInitObject(3, category.Base, parentKey)
                )
            );
        }

        function mapSecondLevelCategory(category: contract.ISecondLevelCategory, parentKey: string): SnapshotOut<typeof CategoryStore> {
            return CategoryStore.create(<SnapshotOut<typeof CategoryStore>>
                Object.assign(
                    {
                        categoriesIds: observable(
                            category.Categories.map(c => {
                                self.categories.set(`${parentKey}c${c.Base.Id}`, mapThirdLevelCategory(c, parentKey));
                                return c.Base.Id;
                            })
                        ),
                        graphicsPresentationId: category.GraphicsPresentationId
                    },
                    mapBaseCategoryToStoreInitObject(2, category.Base),
                    {
                        isVirtual: category.IsVirtual
                    }
                )
            );
        }

        function mapFirstLevelCategory(category: contract.IFirstLevelCategory): string[] {
            return category.Categories.map(c => {
                self.categories.set(c.Base.Id, mapSecondLevelCategory(c, c.Base.Id));
                //mapSecondLevelCategory(c, `${c.Base.Id}`);
                return c.Base.Id;
            });
        }

        const loadAsyncRaw: (params: { from?: Date; to?: Date, canShowLiveInPrematch: boolean }) => Promise<void> = flow(function* (params: { from?: Date; to?: Date, canShowLiveInPrematch: boolean }) {
            // when canUpdateCategoryListSilent is false, it may override vizually updated list of Favorites. Ignored now.
            let ctx: IGroupedApiCtx<ISportsBettingApi> | null = null;
            self.state = ProgressStateModel.Loading;
            try {
                if (synchroCtx && synchroCtx.isOpen()) {
                    synchroCtx.cancel('Load has been canceled.');
                    synchroCtx = null;
                }
                ctx = env.api.open();
                synchroCtx = ctx;
                let paramsForApi: IGetWebStandardCategoriesParams = { from: params.from, to: params.to, includeLiveCategories: params.canShowLiveInPrematch };
                const r: contract.IGetWebStandardCategoriesResponse = yield synchroCtx.api.getWebStandardCategoriesAsync(paramsForApi);
                self.categories.clear();
                if (r != null) {
                    self.rootCategoryIds = observable(mapFirstLevelCategory(r.Categories));
                    self.todayTommorowWeekFilterCounts.today = r.TodayCount;
                    self.todayTommorowWeekFilterCounts.tommorow = r.TomorrowCount;
                    self.todayTommorowWeekFilterCounts.week = r.WeekCount;
                    self.state = ProgressStateModel.Success;
                    self.timeStamp = r.TimeStamp;
                } //TODOH unexpected behaviour if r == null  see also self.categories.clear(); above + also in loadSilent
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in categories list loading.', error);
                self.state = ProgressStateModel.Failed;
                throw error;
            } finally {
                if (synchroCtx === ctx) {
                    synchroCtx = null;
                }
            }
        });

        const loadSilentAsync: (params: { from?: Date; to?: Date, canShowLiveInPrematch: boolean }) => Promise<void> = flow(function* (params: { from?: Date; to?: Date, canShowLiveInPrematch: boolean }) {
            if (!canUpdateCategoryListSilent()) {
                throw new Error("Unable to silently update");
            }
            if (synchroCtx && synchroCtx.isOpen()) {
                throw new CancelError();
            }
            let ctx: IGroupedApiCtx<ISportsBettingApi> | null = null;
            try {
                //TODO what about cats of other levels? +!! seems to be problems with expanded cats (by arrow) + when no cat is active, it shows nothing after update + !! update number of offers
                ctx = env.api.open();
                synchroCtx = ctx;

                let paramsForApi: IGetWebStandardCategoriesParams = { from: params.from, to: params.to, includeLiveCategories: params.canShowLiveInPrematch, timeStamp: self.timeStamp };
                const r: contract.IGetWebStandardCategoriesResponse = yield synchroCtx.api.getWebStandardCategoriesAsync(paramsForApi);

                if (r != null) {
                    if (self.activeCategoryKey) {
                        const parents = self.searchActiveParents(self.activeCategoryKey).reverse();
                        const allIds: string[] = [];

                        self.categories.forEach(c => {
                            allIds.push(c.id);
                        });

                        if (!parents.some(rc => r.Categories.Categories.find(c => c.Base.Id === getCategoryIdFromKey(rc)) !== undefined)) {
                            const notActive = allIds.filter(i => parents.filter(p => p === i).length === 0);
                            notActive.forEach(i => {
                                //removes all existing that are not active? to be re-added below
                                self.categories.delete(`${i}`);
                                //self.expandedCategories.delete(`${i}`);
                            });

                            self.rootCategoryIds = observable(
                                r.Categories.Categories.filter(c => !self.categories.has(`${c.Base.Id}`)) //due to problems with updating of an existing row (duplicate id)
                                    .map(c => {
                                        self.categories.set(`${c.Base.Id}`, mapSecondLevelCategory(c, `${c.Base.Id}`));
                                        return c.Base.Id;
                                    })
                                    .concat(self.categories.has(`${self.activeCategoryKey}`) ? [getCategoryIdFromKey(parents.shift())] : [])
                            );
                        } else {

                            self.categories.clear();
                            self.rootCategoryIds = observable(
                                r.Categories.Categories.map(c => {
                                    self.categories.set(`${c.Base.Id}`, mapSecondLevelCategory(c, `${c.Base.Id}`));
                                    return c.Base.Id;
                                })
                            );
                        }
                    } else {
                        let expandedCategories: string[] = []; //to keep expanded categories
                        self.categories.forEach(cat => { if (cat.expanded) { expandedCategories.push(cat.id) } });

                        self.rootCategoryIds = observable(
                            r.Categories.Categories.map(c => {
                                self.categories.set(`${c.Base.Id}`, mapSecondLevelCategory(c, `${c.Base.Id}`));
                                return c.Base.Id;
                            })
                        );
                        self.categories.forEach(cat => { if (expandedCategories.indexOf(cat.id) > -1) { cat.expanded = true; } });
                    }
                    self.todayTommorowWeekFilterCounts.today = r.TodayCount;
                    self.todayTommorowWeekFilterCounts.tommorow = r.TomorrowCount;
                    self.todayTommorowWeekFilterCounts.week = r.WeekCount;
                    self.timeStamp = r.TimeStamp;
                }
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in categories list refreshing.', error);
                throw error;
            } finally {
                if (synchroCtx === ctx) {
                    synchroCtx = null;
                }
            }
        });

        const toggleCategory = (key: string) => {
            self.expandedCategories.has(key) ? self.expandedCategories.delete(key) : expandCategory(key);
        };

        const expandCategory = (key: string) => {
            self.expandedCategories.set(key, true);
        };

        const openCategory = (key: string) => {
            //expandCategory(key);
            self.activeCategoryKey = key;
        };
        const toggleWithDefault = (value: boolean | null) => {
            return value === null ? true : !value;
            //return value === null ? false : !value;
        };

        const afterFavoriteItemsChangeAsync: (loadProtectionInstance: Object, params: { id: string; type: FavoriteOfferEnum; label: string; favorites?: number }, isAfterDelete: boolean) => Promise<void> = flow(function* (loadProtectionInstance, params, isAfterDelete) {
            var i = favoriteItemsUpdateRunningQueries.indexOf(loadProtectionInstance);
            if (i >= 0) {
                favoriteItemsUpdateRunningQueries.splice(i, 1);
            }

            if (favoriteItemsUpdateRunningQueries.length > 0) {
                return;
            }

            if (isAfterDelete) {
                //pushToEventList also blocks execution in router
                if (params.favorites) {
                    const auxFavorites = Number(params.favorites);
                    pushToEventList({ favorites: auxFavorites === 1 ? 10000 : auxFavorites - 1 }); // fix to call correct api when list empty
                }
            } else {
                if (params.favorites) {
                    pushToEventList({ favorites: Number(params.favorites) + 1 });
                }
            }
            yield loadFavoriteItemsAsync();
        });

        const canUpdateCategoryListSilent = function () {
            return favoriteItemsUpdateRunningQueries.length === 0;
        };

        const toggleFavoriteItemAsync: (params: { id: string; type: FavoriteOfferEnum; label: string; favorites?: number, hasLive: boolean }) => Promise<void> = flow(function* (params: { id: string; type: FavoriteOfferEnum; label: string; favorites?: number, hasLive: boolean }) {
            if (self.isLoggedUser) {
                const itemKey: string = params.id;
                var localCtx = env.api.open();

                var loadProtectionInstance = {};
                favoriteItemsUpdateRunningQueries.push(loadProtectionInstance);   //TODOHX why? maybe due to incorrect asyncs. Maybe wants to get "last"? but ifAfterDelete
                try {
                    if (self.favorites.has(itemKey)) {
                        self.favorites.delete(itemKey);
                        yield localCtx.api.deleteFavoriteOfferAsync(params.id, params.type);
                        yield afterFavoriteItemsChangeAsync(loadProtectionInstance, params, true);
                    } else {
                        self.favorites.set(itemKey, {
                            name: params.label,
                            iconId: null,
                            id: params.id,
                            path: observable([params.label]),
                            type: params.type,
                            isLoading: true,
                            hasLive: params.hasLive
                        });
                        try {
                            yield localCtx.api.addFavoriteOfferAsync(params.id, params.type);
                        } catch (error) {
                            self.favorites.delete(itemKey);
                        }
                        yield afterFavoriteItemsChangeAsync(loadProtectionInstance, params, false);
                    }
                } finally {
                    var i = favoriteItemsUpdateRunningQueries.indexOf(loadProtectionInstance);
                    if (i >= 0) {
                        favoriteItemsUpdateRunningQueries.splice(i, 1);
                    }
                }
            }
        });

        const loadFavoriteItemsAsync: () => Promise<void> = flow(function* () {
            if (self.isLoggedUser) {
                let localCtx = env.api.open();

                const r: FavoriteOffers = yield localCtx.api.getFavoriteOffersAsync(env.settings.canShowLiveInPrematch);

                self.favorites.clear();
                if (r.Categories) {
                    r.Categories.map(category => {
                        self.favorites.set(category.Id, {
                            name: category.Name,
                            iconId: category.GraphicsPresentationId,
                            id: category.Id,
                            path: observable([]) /* get from category list */ /*observable(category.CategoryPathName.split('/'))*/,
                            type: FavoriteOfferEnum.Category,
                            isLoading: false,
                            hasLive: category.HasLive
                        });
                    });
                }
                if (r.Events) {
                    r.Events.map(event => {
                        self.favorites.set(`${event.Id}`, {
                            name: event.Name,
                            iconId: event.GraphicsPresentationId,
                            id: `${event.Id}`,
                            path: observable([]) /* get from category list */ /*observable(event.CategoryPathName ? event.CategoryPathName.split('/') : [])*/,
                            type: FavoriteOfferEnum.Event,
                            isLoading: false,
                            hasLive: event.IsLive
                        });
                    });
                }

                //CancelError not expected
            }
        });

        const toggleFavoriteExpanded = () => {
            self.isFavoritesExpanded = !self.isFavoritesExpanded;
        };

        const initAsync: (params: IPushToEventListParams) => Promise<void> = flow(function* (params: IPushToEventListParams) {

            prepareFilterBarZones();

            const { categoryKey: categoryId } = params;
            const dateRange = prepareFromTo(params, env.dateTimeService);
            const rates = prepareRates(params);
            let rateFrom = RateFilterMinOddsRateInInput;
            if (!!rates.rateFrom) {
                rateFrom = rates.rateFrom;
            }
            setRateFrom(rateFrom);
            let rateTo = RateFilterMaxOddsRateInInput;
            if (!!rates.rateTo) {
                rateTo = rates.rateTo;
            }
            setRateTo(rateTo);

            yield loadAsyncRaw({
                from: dateRange.from,
                to: dateRange.to,
                canShowLiveInPrematch: getCanShowLiveInPrematchCommon(env.settings.canShowLiveInPrematch, dateRange, rates, null)
            });
            if (categoryId) {
                openCategory(categoryId);
            }
        });

        const setRateFrom = (value: number) => {
            self.rateFrom = value;
            setRateFromInInput(value);
        };
        const setRateTo = (value: number) => {
            self.rateTo = value;
            setRateToInInput(value);
        };

        const setRateFromInInput = (value: number) => {
            self.rateFromInInput = value;
            self.rateFromInBar = convertInputToBar(value);
        };

        const setRateToInInput = (value: number) => {
            self.rateToInInput = value;
            self.rateToInBar = convertInputToBar(value);
        };

        const setRateFromInBar = (value: number) => {
            self.rateFromInBar = value;
            self.rateFromInInput = convertBarToInput(value);
        };

        const setRateToInBar = (value: number) => {
            self.rateToInBar = value;
            self.rateToInInput = convertBarToInput(value);
        };

        const useRatesInInput = () => {
            if (self.rateFromInInput === null || self.rateFromInInput < RateFilterMinOddsRateInInput) {
                self.rateFromInInput = RateFilterMinOddsRateInInput;
            }
            if (self.rateToInInput === null || self.rateToInInput > RateFilterMaxOddsRateInInput) {
                self.rateToInInput = RateFilterMaxOddsRateInInput;
            }
            if (self.rateFromInInput > self.rateToInInput) {
                const from = self.rateFromInInput;
                self.rateFromInInput = self.rateToInInput;
                self.rateToInInput = from;
            }
            self.rateFrom = self.rateFromInInput;
            self.rateTo = self.rateToInInput;
        };

        const addRatesToParams = (params: IPushToEventListParams) => {
            if (!!self.rateFrom && self.rateFrom != RateFilterMinOddsRateInInput) {
                params.rateFrom = self.rateFrom;
            }
            if (!!self.rateTo && self.rateTo != RateFilterMaxOddsRateInInput) {
                params.rateTo = self.rateTo;
            }
        };

        const setEnabledCalendarDays: () => Promise<void> = flow(function* () {
            let localCtx = env.api.open();

            const enabledCalendarDays: string[] = yield localCtx.api.getDaysWithActiveEvents()
            if (enabledCalendarDays) {
                self.enabledCalendarDays = observable(enabledCalendarDays.map(day => <Instance<typeof types.string>>(day)));
            }
        });

        return {
            initAsync,
            loadAsync: (params: IPushToEventListParams): Promise<void> => {
                const dateRange = prepareFromTo(params, env.dateTimeService);

                return loadAsyncRaw({
                    from: dateRange.from,
                    to: dateRange.to,
                    canShowLiveInPrematch: getCanShowLiveInPrematchCommon(env.settings.canShowLiveInPrematch, dateRange, prepareRates(params), null)
                })
            },
            refreshAsync: (params: IPushToEventListParams): Promise<void> => {
                const dateRange = prepareFromTo(params, env.dateTimeService);
                return loadSilentAsync({
                    from: dateRange.from,
                    to: dateRange.to,
                    canShowLiveInPrematch: getCanShowLiveInPrematchCommon(env.settings.canShowLiveInPrematch, dateRange, prepareRates(params), null)
                });
            },
            loadFavoriteItemsAsync,
            openCategory,
            toggleCategory,
            toggleFavoriteItemAsync,
            toggleFavoriteExpanded,
            goToAllEventList: () => {
                const params = <IPushToEventListParams>{ categoryKey: rootCategoryId };
                addRatesToParams(params);
                pushToEventList(params);
            },
            gotoCategoryEventList: (params: { categoryKey: string; filter?: number; filterParams?: IFilterParams; searchBy?: string }) => {
                addRatesToParams(params);
                pushToEventList(params);
                openCategory(params.categoryKey);
            },
            gotoCategoryEventListWithRefresh: (categoryKey: string) => {
                var data = { categoryKey: categoryKey };
                addRatesToParams(data);
                pushToEventList(data);
                openCategory(categoryKey);
            },
            goToTodayEventList: () => {
                const dateParams = { categoryKey: rootCategoryId, filter: FilterType.today };
                addRatesToParams(dateParams);
                pushToEventList(dateParams);
            },
            goToTommorowEventList: () => {
                const dateParams = {
                    categoryKey: rootCategoryId,
                    filter: FilterType.tommorow
                };
                addRatesToParams(dateParams);
                pushToEventList(dateParams);
            },
            goToNextWeekEventList: () => {
                const dateParams = {
                    categoryKey: rootCategoryId,
                    filter: FilterType.nextWeek
                };
                addRatesToParams(dateParams);
                pushToEventList(dateParams);
            },
            goToDay: (params: { date: Date }) => {
                const dateParams = {
                    categoryKey: rootCategoryId,
                    filter: FilterType.byDay,
                    filterParams: { date: new Date(params.date.getFullYear(), params.date.getMonth(), params.date.getDate()) }
                };
                addRatesToParams(dateParams);
                pushToEventList(dateParams);
            },
            goToWeekDay: (params: { date: Date }) => {
                const dateParams = {
                    categoryKey: rootCategoryId,
                    filter: FilterType.byWeekDay,
                    filterParams: { date: params.date }
                };
                addRatesToParams(dateParams);
                pushToEventList(dateParams);
            },
            goToHours: (params: { hours: number }) => {
                const dateParams = {
                    categoryKey: rootCategoryId,
                    filter: FilterType.byTime,
                    filterParams: { hours: params.hours }
                };
                addRatesToParams(dateParams);
                pushToEventList(dateParams);
            },
            goToTimeDate: (params: TimeDateFilterOptions, categoryId: string) => {
                const dateParams = {
                    categoryKey: categoryId,
                    filter: FilterType.timeDateFilter,
                    filterParams: { timeDateFilterValue: params }
                };
                addRatesToParams(dateParams);
                pushToEventList(dateParams);
            },

            goToRates: (routeParams: IPushToEventListParams) => {
                let params = { ...routeParams };
                params.rateFrom = undefined;
                params.rateTo = undefined;
                addRatesToParams(params);
                pushToEventList(params);
            },

            toggleExpendingOfWeekDayFilter: () => {
                self.isExpendedWeekDayFilter = toggleWithDefault(self.isExpendedWeekDayFilter);
            },
            toggleExpandedDateFilter: () => {
                self.isExpandedDateFilter = toggleWithDefault(self.isExpandedDateFilter);
            },
            setExpandedTimeFilter: (value: boolean) => {
                self.isExpandedTimeFilter = value;
            },
            toggleExpendedMoreFilter: () => {
                self.isExpendedMoreFilter = toggleWithDefault(self.isExpendedMoreFilter);
            },
            setRateFrom,
            setRateTo,
            setRateFromInInput,
            setRateToInInput,
            useRatesInInput,
            setRateFromInBar,
            setRateToInBar,
            setEnabledCalendarDays,
        };
    });

export const getCategoryIdFromKey = (key: string): string => {
    let s = key.split('c');
    return s[s.length - 1];
};

export const getEventDetailParamFromKey = (key: string): { eventId: number; categoryId?: string } => {
    let s = key.split('c');
    if (s.length === 1) {
        return {
            eventId: Number(s[0])
        };
    }

    return {
        eventId: Number(s[0]),
        categoryId: s[1]
    };
};

let filterBarZones: { min: number, max: number }[];

export let filterBarMin: number;
export let filterBarMax: number;

const prepareFilterBarZones = () => {
    let lastLimit = 0;
    const result: { min: number, max: number }[] = [];
    RateFilterInputZones.forEach((item) => {
        const steps = Math.floor((item.max - item.min) / item.step);
        const newMax = lastLimit + steps;
        result.push({ min: lastLimit, max: newMax });
        lastLimit = newMax + 1;
    });
    filterBarZones = result;
    filterBarMin = 0;
    filterBarMax = lastLimit - 1;
};

const convertInputToBar = (value: number): number => {
    let index = 0;
    let zone = RateFilterInputZones[index];
    while (!(zone.min <= value && zone.max >= value)) {
        if (index + 1 >= RateFilterInputZones.length) {
            return filterBarMax;
        }
        index++;
        zone = RateFilterInputZones[index];
    }
    return Math.floor(filterBarZones[index].min + (value - zone.min) / zone.step);
};

const convertBarToInput = (value: number) => {
    let index = 0;
    let zone = filterBarZones[index];
    while (!(zone.min <= value && zone.max >= value)) {
        if (index + 1 >= filterBarZones.length) {
            return RateFilterMaxOddsRateInInput;
        }
        index++;
        zone = filterBarZones[index];
    }
    const inputZone = RateFilterInputZones[index];
    let result = inputZone.min + (value - zone.min) * inputZone.step;
    return Math.round(result * 100) / 100;
}
